import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';

/**
 * API Client with Axios
 * Handles authentication, error handling, and token refresh
 */
// const API_BASE_URL ='http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor - Attach auth token
 */
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handle errors and token refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (!refreshToken) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Try to refresh token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken: newAccessToken } = response.data.data;
        useAuthStore.getState().setAccessToken(newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle errors
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'An error occurred. Please try again.';

    if (error.response?.status !== 401) {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

/**
 * API Service Methods
 */
export const authAPI = {
  login: (email, password) =>
    apiClient.post('/auth/login', { email, password }),
  logout: () => apiClient.post('/auth/logout'),
  getCurrentUser: () => apiClient.get('/auth/me'),
  refreshToken: (refreshToken) =>
    apiClient.post('/auth/refresh', { refreshToken }),
};

export const channelAPI = {
  getAll: (page = 1, limit = 10, category = '', country = '', search = '') =>
    apiClient.get('/channels', {
      params: { page, limit, category, country, search },
    }),
  getById: (id) => apiClient.get(`/channels/${id}`),
  create: (data) => apiClient.post('/channels', data),
  update: (id, data) => apiClient.put(`/channels/${id}`, data),
  delete: (id) => apiClient.delete(`/channels/${id}`),
  bulkUpdate: (updates) => apiClient.post('/channels/bulk/update', { updates }),
};

export const configAPI = {
  get: () => apiClient.get('/config'),
  update: (data) => apiClient.put('/config', data),
  toggleFeature: (featureName, enabled) =>
    apiClient.post(`/config/features/${featureName}`, { enabled }),
  toggleMaintenance: (maintenance, message) =>
    apiClient.post('/config/maintenance', { maintenance, message }),
  switchAPI: (apiUrl) => apiClient.post('/config/api-switch', { apiUrl }),
};

export const deviceAPI = {
  register: (data) => apiClient.post('/device/register', data),
  getAll: (page = 1, limit = 100, isActive = '', country = '') =>
    apiClient.get('/device', { params: { page, limit, isActive, country } }),
  getById: (deviceId) => apiClient.get(`/device/${deviceId}`),
  getStats: () => apiClient.get('/device/stats/summary'),
  addFavorite: (deviceId, channelId) =>
    apiClient.post(`/device/${deviceId}/favorites`, { channelId }),
  trackWatchHistory: (deviceId, channelId, duration) =>
    apiClient.post(`/device/${deviceId}/watch-history`, {
      channelId,
      duration,
    }),
};

export const versionAPI = {
  getLatest: () => apiClient.get('/version/latest'),
  getAll: (page = 1, limit = 10) =>
    apiClient.get('/version', { params: { page, limit } }),
  create: (data) => apiClient.post('/version', data),
  update: (id, data) => apiClient.put(`/version/${id}`, data),
  checkForUpdate: (versionCode) =>
    apiClient.get(`/version/check/${versionCode}`),
  recordDownload: (id) => apiClient.post(`/version/${id}/download`),
};

export const subscriptionPackageAPI = {
  getAll: (page = 1, limit = 20, search = '', status = '') =>
    apiClient.get('/subscriptions/packages', {
      params: { page, limit, search, status },
    }),
  create: (data) => apiClient.post('/subscriptions/packages', data),
  update: (id, data) => apiClient.put(`/subscriptions/packages/${id}`, data),
  delete: (id) => apiClient.delete(`/subscriptions/packages/${id}`),
  toggleStatus: (id, status) =>
    apiClient.patch(`/subscriptions/packages/${id}/status`, { status }),
};

export const subscriptionRequestAPI = {
  create: (data) => apiClient.post('/subscriptions/requests', data),
  getAll: (page = 1, limit = 20, status = '', search = '', type = '') =>
    apiClient.get('/subscriptions/requests', {
      params: { page, limit, status, search, type },
    }),
  approve: (id) => apiClient.post(`/subscriptions/requests/${id}/approve`),
  reject: (id) => apiClient.post(`/subscriptions/requests/${id}/reject`),
};

export const subscriptionHistoryAPI = {
  getAll: (page = 1, limit = 20, status = '', search = '', startDate = '', endDate = '') =>
    apiClient.get('/subscriptions/history', {
      params: { page, limit, status, search, startDate, endDate },
    }),
  exportCSV: (params) =>
    apiClient.get('/subscriptions/history/export', {
      params,
      responseType: 'blob',
    }),
};

export const paymentMethodAPI = {
  getAll: (page = 1, limit = 20, search = '', status = '') =>
    apiClient.get('/payment-methods', {
      params: { page, limit, search, status },
    }),
  create: (data) => apiClient.post('/payment-methods', data),
  update: (id, data) => apiClient.put(`/payment-methods/${id}`, data),
  delete: (id) => apiClient.delete(`/payment-methods/${id}`),
  toggleStatus: (id, status) => apiClient.patch(`/payment-methods/${id}/status`, { status }),
};

// export const webPayAPI = {
//   get: () => apiClient.get('/webpay'),
//   create: (data) => apiClient.post('/webpay', data),
//   update: (data) => apiClient.put('/webpay', data),
//   delete: (id) => apiClient.delete(`/webpay/${id}`),
// };

export const webPayAPI = {
  get: () => apiClient.get('/webpay'),

  create: (data) => apiClient.post('/webpay', data),

  update: (data) => apiClient.put('/webpay', data),

  delete: () => apiClient.delete('/webpay'),
};

export const analyticsAPI = {
  getDashboardStats: () => apiClient.get('/analytics/dashboard'),
  getUserGrowth: (days = 30) =>
    apiClient.get('/analytics/user-growth', { params: { days } }),
  getChannelViews: (limit = 10) =>
    apiClient.get('/analytics/channel-views', { params: { limit } }),
  getDevicesByCountry: () => apiClient.get('/analytics/devices-by-country'),
  getDevicesByOS: () => apiClient.get('/analytics/devices-by-os'),
  getAPIPerformance: (days = 7) =>
    apiClient.get('/analytics/api-performance', { params: { days } }),
  getTopErrors: (limit = 10) =>
    apiClient.get('/analytics/top-errors', { params: { limit } }),
  getSubscriptionSummary: () => apiClient.get('/analytics/subscription-summary'),
  getSubscriptionRevenue: (days = 30) =>
    apiClient.get('/analytics/subscription-revenue', { params: { days } }),
  getSubscriptionGrowth: (days = 30) =>
    apiClient.get('/analytics/subscription-growth', { params: { days } }),
  getDeviceStatusChart: () => apiClient.get('/analytics/device-status-chart'),
};

export default apiClient;
